/**
 * Invitation Acceptance Page
 *
 * Handles viewing and accepting organization invitations.
 */

import { createFileRoute, useNavigate } from '@tanstack/react-router';
import { useState, useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { roleKeyFallback } from '@/lib/role-key-fallback';
import { Loader2, CheckCircle2, XCircle, AlertCircle } from 'lucide-react';
import { formatDateShort } from '@/lib/utils';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { SkeletonCard } from '@/components/ui/skeleton';
import { useToast } from '@/contexts/ToastContext';
import { logError } from '@/lib/logger';
import { getCSRFToken } from '@/lib/csrf';
import { API_BASE_URL } from '@/lib/api/config';

const API_URL = API_BASE_URL;

interface InvitationData {
  invitation: {
    id: string;
    email: string;
    role: string;
    status?: string;
    organization: {
      id: string;
      name: string;
    };
    inviter: {
      name: string;
      email: string;
    } | null;
    expiresAt: string;
    createdAt: string;
  };
}

function InvitationPage() {
  const { token } = Route.useParams();
  const navigate = useNavigate();
  const { toast } = useToast();
  const { t } = useTranslation(['settings', 'common']);

  const [loading, setLoading] = useState(true);
  const [accepting, setAccepting] = useState(false);
  const [invitation, setInvitation] = useState<InvitationData | null>(null);
  const [error, setError] = useState<string | null>(null);
  const redirectTimerRef = useRef<NodeJS.Timeout | undefined>(undefined);

  // Fetch invitation details on mount
  useEffect(() => {
    const abortController = new AbortController();
    let ignore = false;

    async function fetchInvitation() {
      try {
        setLoading(true);
        setError(null);

        const response = await fetch(`${API_URL}/api/v1/invitations/${token}`, {
          signal: abortController.signal,
        });
        if (ignore) return;

        if (!response.ok) {
          const data = (await response.json()) as { error?: string };
          if (ignore) return;

          if (response.status === 404) {
            setError(t('invitations.notFound'));
          } else if (response.status === 410) {
            setError(data.error || t('invitations.expiredOrAccepted'));
          } else {
            setError(data.error || t('invitations.failedToLoad'));
          }
          return;
        }

        const data = (await response.json()) as InvitationData;
        if (ignore) return;
        setInvitation(data);
      } catch (err) {
        if (ignore) return;

        // Do NOT pass `token` here — it's a bearer credential (7-day org
        // invite), and logError forwards this context to Sentry `extra`
        // verbatim (see docs/audit/2026-07-31-egress-ui-worker-sentry.md §5.1).
        // The route itself already identifies the page without it.
        logError('invitations.fetch_failed', {
          component: 'InvitationsPage',
          error: err instanceof Error ? err.message : 'unknown_error',
        });
        setError(err instanceof Error ? err.message : t('invitations.failedToLoad'));
      } finally {
        if (!ignore) setLoading(false);
      }
    }

    void fetchInvitation();

    return () => {
      ignore = true;
      abortController.abort();
    };
  }, [token, t]);

  // Cleanup redirect timer on unmount
  useEffect(() => {
    return () => {
      if (redirectTimerRef.current) {
        clearTimeout(redirectTimerRef.current);
      }
    };
  }, []);

  const handleAccept = async () => {
    try {
      setAccepting(true);
      setError(null);

      // Get CSRF token for protected POST request
      const csrfToken = await getCSRFToken();

      const response = await fetch(`${API_URL}/api/v1/invitations/${token}/accept`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'X-CSRF-Token': csrfToken,
        },
        credentials: 'include',
      });

      if (!response.ok) {
        const data = (await response.json()) as { error?: string };
        setError(data.error || t('invitations.failedToAccept'));
        return;
      }

      await response.json(); // Response validated, data not needed (void to satisfy linter)

      toast.success(t('invitations.acceptedTitle'), t('invitations.acceptedMessage'));

      // Redirect to sign-up or dashboard
      // User should sign in with their email to access the organization
      redirectTimerRef.current = setTimeout(() => {
        // Redirect to dashboard (sign-in handled by Clerk)
        window.location.href = '/dashboard';
      }, 2000);
    } catch (err) {
      // See the fetch_failed call above — `token` must not be logged.
      logError('invitations.accept_failed', {
        component: 'InvitationsPage',
        error: err instanceof Error ? err.message : 'unknown_error',
      });
      setError(err instanceof Error ? err.message : t('invitations.failedToAccept'));
    } finally {
      setAccepting(false);
    }
  };

  if (loading) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-bg p-6">
        <div className="w-full max-w-md">
          <SkeletonCard showAvatar={true} lines={4} />
        </div>
      </div>
    );
  }

  if (error && !invitation) {
    return (
      <div className="flex min-h-screen items-center justify-center">
        <Card className="w-full max-w-md p-6">
          <div className="text-center">
            <XCircle className="mx-auto h-12 w-12 text-danger" />
            <h1 className="mt-4 text-xl font-semibold text-fg">
              {t('invitations.invitationError')}
            </h1>
            <p className="mt-2 text-sm text-fg-muted">{error}</p>
            <Button
              onClick={() => navigate({ to: '/dashboard' })}
              className="mt-6"
              variant="outline"
            >
              {t('invitations.goToSignIn')}
            </Button>
          </div>
        </Card>
      </div>
    );
  }

  if (!invitation) {
    return null;
  }

  const { invitation: inv } = invitation;
  const expiresAt = new Date(inv.expiresAt);
  const isExpired = expiresAt < new Date();

  return (
    <div className="flex min-h-screen items-center justify-center bg-bg p-4">
      <Card className="w-full max-w-md p-6">
        <div className="text-center">
          {inv.status === 'accepted' ? (
            <>
              <CheckCircle2 className="mx-auto h-12 w-12 text-success" />
              <h1 className="mt-4 text-xl font-semibold text-fg">
                {t('invitations.invitationAccepted')}
              </h1>
              <p className="mt-2 text-sm text-fg-muted">
                {t('invitations.alreadyAcceptedMessage')}
              </p>
              <Button
                onClick={() => navigate({ to: '/dashboard' })}
                className="mt-6"
                variant="outline"
              >
                {t('invitations.goToSignIn')}
              </Button>
            </>
          ) : isExpired ? (
            <>
              <AlertCircle className="mx-auto h-12 w-12 text-warning" />
              <h1 className="mt-4 text-xl font-semibold text-fg">
                {t('invitations.invitationExpired')}
              </h1>
              <p className="mt-2 text-sm text-fg-muted">
                {t('invitations.expiredMessage')}
              </p>
              <Button
                onClick={() => navigate({ to: '/dashboard' })}
                className="mt-6"
                variant="outline"
              >
                {t('invitations.goToSignIn')}
              </Button>
            </>
          ) : (
            <>
              <h1 className="text-2xl font-semibold text-fg">
                {t('invitations.youveBeenInvited')}
              </h1>
              <p className="mt-2 text-sm text-fg-muted">
                {inv.inviter
                  ? t('invitations.invitedByPerson', { name: inv.inviter.name })
                  : t('invitations.youHaveBeenInvited')}{' '}
                {t('invitations.toJoinOrg', { org: inv.organization.name })}
              </p>

              <div className="mt-6 space-y-3 rounded-card border border-bg-elevated bg-bg-surface p-4 text-left">
                <div>
                  <p className="text-xs font-medium text-fg-muted">
                    {t('invitations.organization')}
                  </p>
                  <p className="text-sm text-fg">{inv.organization.name}</p>
                </div>
                <div>
                  <p className="text-xs font-medium text-fg-muted">
                    {t('invitations.yourEmail')}
                  </p>
                  <p className="text-sm text-fg">{inv.email}</p>
                </div>
                <div>
                  <p className="text-xs font-medium text-fg-muted">
                    {t('invitations.role')}
                  </p>
                  <p className="text-sm text-fg">
                    {t(`common:roles.${inv.role}`, {
                      defaultValue: roleKeyFallback(inv.role),
                    })}
                  </p>
                </div>
                {inv.inviter && (
                  <div>
                    <p className="text-xs font-medium text-fg-muted">
                      {t('invitations.invitedBy')}
                    </p>
                    <p className="text-sm text-fg">{inv.inviter.name}</p>
                  </div>
                )}
                <div>
                  <p className="text-xs font-medium text-fg-muted">
                    {t('invitations.expires')}
                  </p>
                  <p className="text-sm text-fg">{formatDateShort(expiresAt)}</p>
                </div>
              </div>

              <div className="mt-6 flex gap-3">
                <Button
                  onClick={handleAccept}
                  disabled={accepting}
                  className="flex-1"
                  variant="default"
                >
                  {accepting ? (
                    <>
                      <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                      {t('invitations.accepting')}
                    </>
                  ) : (
                    t('invitations.acceptInvitation')
                  )}
                </Button>
                <Button
                  onClick={() => navigate({ to: '/dashboard' })}
                  variant="outline"
                  className="flex-1"
                >
                  {t('invitations.cancel')}
                </Button>
              </div>

              {error && (
                <div className="mt-4 rounded-card border border-danger bg-danger-dim p-3 text-sm text-danger-fg">
                  {error}
                </div>
              )}
            </>
          )}
        </div>
      </Card>
    </div>
  );
}

export const Route = createFileRoute('/invitations/$token')({
  component: InvitationPage,
});
